// Device data for staff (VisionPlatform old-device-import HI-01 … HI-10, vision-api 0.78.0): data merge imports onto
// a device, full and CSV exports, the full device import into an organisation, and CreaNod1-Clone's connection
// (OD-12 … OD-14). Every data route needs the capability `device.data`; the clone routes `bridge.manage`.
import { queryOptions } from "@tanstack/react-query";
import { apiBase, apiClient, unwrap } from "../../../api/client";
import type { components } from "../../../api/generated/vision-api";
import { isProblem, toApiError } from "../../../api/problem";
import { accessToken } from "../../../auth/token";

export type DataImport = components["schemas"]["DataImport"];
export type DataImportColumn = components["schemas"]["DataImportColumn"];
export type DeviceExport = components["schemas"]["DeviceExport"];
export type DeviceImport = components["schemas"]["DeviceImport"];
export type CloneConnection = components["schemas"]["CloneConnection"];
export type CloneCreate = components["schemas"]["CloneCreate"];
export type CloneConnectionInput = components["schemas"]["CloneConnectionInput"];
type GrantLike = { capability: string };

/** How often a check, an import or an export is read again while it works. */
export const DATA_POLL_MS = 2_000;
/** The largest file an import takes (DDT-06). */
export const MAX_DATA_BYTES = 500 * 1024 * 1024;

const ops = () => apiClient("ops");

/** Staff with `device.data` (platform admins) import and export device data (HI-01). The API decides; this only hides. */
export function canMoveData(grants: readonly GrantLike[] | undefined): boolean {
  return (grants ?? []).some((g) => g.capability === "device.data");
}

export const dataImportWorking = (i: DataImport) => i.status === "checking" || i.status === "running";
export const exportWorking = (e: DeviceExport) => e.status === "queued" || e.status === "running";
export const deviceImportWorking = (i: DeviceImport) =>
  i.status === "uploaded" || i.status === "validating" || i.status === "running";

export const dataImportsQuery = (deviceId: string) =>
  queryOptions({
    queryKey: ["ops", "/v1/ops/devices/{device_id}/data-imports", deviceId],
    queryFn: () =>
      unwrap(ops().GET("/v1/ops/devices/{device_id}/data-imports", { params: { path: { device_id: deviceId } } })),
    refetchInterval: (q) => (q.state.data?.items.some(dataImportWorking) ? DATA_POLL_MS : false),
  });

export function startDataImport(id: string): Promise<DataImport> {
  return unwrap(ops().POST("/v1/ops/data-imports/{import_id}/start", { params: { path: { import_id: id } } }));
}

export async function discardDataImport(id: string): Promise<void> {
  await unwrap(ops().DELETE("/v1/ops/data-imports/{import_id}", { params: { path: { import_id: id } } }));
}

export const deviceExportQuery = (id: string) =>
  queryOptions({
    queryKey: ["ops", "/v1/ops/device-exports/{export_id}", id],
    queryFn: () => unwrap(ops().GET("/v1/ops/device-exports/{export_id}", { params: { path: { export_id: id } } })),
    refetchInterval: (q) => (q.state.data && exportWorking(q.state.data) ? DATA_POLL_MS : false),
  });

export function startExport(
  deviceId: string,
  format: "full" | "csv",
  from?: string,
  to?: string,
): Promise<DeviceExport> {
  return unwrap(
    ops().POST("/v1/ops/devices/{device_id}/exports", {
      params: { path: { device_id: deviceId } },
      body: { format, ...(format === "csv" && from ? { from } : {}), ...(format === "csv" && to ? { to } : {}) },
    }),
  );
}

/** A ready export's file, read whole: the download needs the token, so a plain link cannot fetch it. */
export async function downloadExport(id: string): Promise<Blob> {
  const { data, error, response } = await ops().GET("/v1/ops/device-exports/{export_id}/file", {
    params: { path: { export_id: id } },
    parseAs: "blob",
  });
  if (!response.ok) throw toApiError(response.status, error, response.headers.get("Retry-After"));
  return data as Blob;
}

export const deviceImportQuery = (id: string) =>
  queryOptions({
    queryKey: ["ops", "/v1/ops/device-imports/{import_id}", id],
    queryFn: () => unwrap(ops().GET("/v1/ops/device-imports/{import_id}", { params: { path: { import_id: id } } })),
    refetchInterval: (q) => (q.state.data && deviceImportWorking(q.state.data) ? DATA_POLL_MS : false),
  });

export type Uploaded<T> = { ok: true; body: T } | { ok: false; status: number; code?: string; detail?: string };

export function uploaded<T>(status: number, text: string): Uploaded<T> {
  let body: unknown;
  try {
    body = JSON.parse(text);
  } catch {
    body = undefined;
  }
  if (status === 202 && typeof body === "object" && body !== null) return { ok: true, body: body as T };
  return {
    ok: false,
    status,
    ...(isProblem(body) ? { code: body.code, ...(body.detail ? { detail: body.detail } : {}) } : {}),
  };
}

/**
 * Posts a file (and text fields) to a console route with XMLHttpRequest, because fetch cannot report upload progress.
 * Same base URL and token as the generated client; ops routes take no X-Org.
 */
export async function uploadData<T>(
  path: string,
  file: File,
  fields: Record<string, string>,
  onProgress: (sent: number, total: number) => void,
  XHR: typeof XMLHttpRequest = XMLHttpRequest,
): Promise<Uploaded<T>> {
  const token = await accessToken("ops");
  const body = new FormData();
  for (const [k, v] of Object.entries(fields)) if (v) body.append(k, v);
  body.append("file", file, file.name);
  return new Promise((resolve, reject) => {
    const xhr = new XHR();
    xhr.open("POST", `${apiBase("ops")}${path}`);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total);
    };
    xhr.onerror = () => reject(new Error("network"));
    xhr.onload = () => resolve(uploaded<T>(xhr.status, xhr.responseText));
    xhr.send(body);
  });
}

export const uploadDataImport = (deviceId: string, file: File, onProgress: (s: number, t: number) => void) =>
  uploadData<DataImport>(`/v1/ops/devices/${encodeURIComponent(deviceId)}/data-imports`, file, {}, onProgress);

export const uploadDeviceImport = (
  file: File,
  orgId: string,
  spaceId: string,
  onProgress: (s: number, t: number) => void,
) => uploadData<DeviceImport>("/v1/ops/device-imports", file, { org_id: orgId, space_id: spaceId }, onProgress);

/** The i18n key of an import's or export's error code; unknown codes read as a general failure. */
export function dataErrorKey(code: string | null | undefined): string {
  const known = [
    "bad_file",
    "product_mismatch",
    "too_large",
    "write_failed",
    "expired",
    "not_claimed",
    "not_checked",
    "not_discardable",
    "import_running",
    "device_elsewhere",
    "device_in_stock",
    "unknown_product",
    "too_many_topics",
    "bridge_unavailable",
  ];
  return `deviceData.errors.${code && known.includes(code) ? code : "other"}`;
}

/** What a column of a checked file fills, as one line: `air_temperature (air_temperature_c_x10, sensor 2) · shown`. */
export function columnTarget(c: DataImportColumn): string {
  if (c.status !== "mapped") return "";
  const parts = [c.field, c.instance && c.instance > 1 ? `#${c.instance}` : ""].filter(Boolean).join(" ");
  return `${c.datapoint ?? ""}${parts ? ` (${parts})` : ""}`;
}

// ---------------------------------------------------------------------------------------------------------------
// CreaNod1-Clone (OD-12 … OD-14).

export const cloneConnectionQuery = (deviceId: string) =>
  queryOptions({
    queryKey: ["ops", "/v1/ops/clones/{device_id}/connection", deviceId],
    queryFn: () =>
      unwrap(ops().GET("/v1/ops/clones/{device_id}/connection", { params: { path: { device_id: deviceId } } })),
    retry: false,
  });

export function createClone(body: CloneCreate): Promise<CloneConnection> {
  return unwrap(ops().POST("/v1/ops/clones", { body }));
}

export function updateCloneConnection(deviceId: string, body: CloneConnectionInput): Promise<CloneConnection> {
  return unwrap(
    ops().PUT("/v1/ops/clones/{device_id}/connection", { params: { path: { device_id: deviceId } }, body }),
  );
}

/** A broker as typed: an existing bridge, or a new one (host, port, TLS, user, password). */
export type BrokerForm = {
  mode: "existing" | "new";
  bridgeId: string;
  host: string;
  port: string;
  tls: boolean;
  username: string;
  password: string;
};

export const emptyBroker = (): BrokerForm => ({
  mode: "new",
  bridgeId: "",
  host: "",
  port: "1883",
  tls: false,
  username: "",
  password: "",
});

/** The broker's URL, `mqtt://host:port` or `mqtts://host:port`. */
export function brokerUrl(f: BrokerForm): string {
  return `${f.tls ? "mqtts" : "mqtt"}://${f.host.trim()}:${f.port.trim()}`;
}

/** A topic is the device's own: no wildcards (OD-12). */
export const validTopic = (t: string): boolean => t.trim().length > 0 && t.length <= 512 && !/[+#]/.test(t);

/** What is wrong with a clone's connection, as i18n keys. */
export function connectionProblems(f: BrokerForm, topic: string): string[] {
  const out: string[] = [];
  if (f.mode === "existing" && !f.bridgeId) out.push("deviceData.clone.problem.broker");
  if (f.mode === "new") {
    if (!/^[A-Za-z0-9.-]+$/.test(f.host.trim())) out.push("deviceData.clone.problem.host");
    const port = Number(f.port);
    if (!Number.isInteger(port) || port < 1 || port > 65535) out.push("deviceData.clone.problem.port");
  }
  if (!validTopic(topic)) out.push("deviceData.clone.problem.topic");
  return out;
}

/** The connection part of a create or change. */
export function connectionBody(f: BrokerForm, topic: string): CloneConnectionInput {
  if (f.mode === "existing") return { bridge_id: f.bridgeId, topic: topic.trim() };
  return {
    source: {
      url: brokerUrl(f),
      ...(f.username.trim() ? { username: f.username.trim() } : {}),
      ...(f.password ? { password: f.password } : {}),
    },
    topic: topic.trim(),
  };
}
